import {setFeedbackModalIsActive} from '../../../stores/feedback'
import {PenIcon} from '../../../icons'
import Feedback from '../../core/feedback/feedback'
import {useDispatch, useSelector} from 'react-redux'
import InfoLine from '../InfoLine'
import Sidebar from '../sidebar'
import ContentLoader from '../../core/loader'
import Alert from '../../core/alert'
import {VoucherModal} from './modals'
import SubscriptionSession from './SubscriptionSession'
import {useEffect, useRef, useState} from 'react'
import {resetQuery} from '../../../stores/monitoring'
import Authorization from './Authorization'
import {routes} from '../../../routes'
import {envConfig} from '../../../utils/env'
import Modal from '../../core/modal'
import {setDisableTutorial, setVoucherData, setVoucherModalIsActive} from '../../../stores/voucher'
import {Link, useLocation} from 'react-router-dom'
import localStorageKeys from '../../../utils/localStorageKeys'
import {DateFormats, formatDate, formatWithGMT} from '../../../utils/date'
import {logCustomerIsActive} from '../../../stores/customer-settings'
import classNames from 'classnames'
import localStorageManager from '../../../utils/localStorageManager'
import plans from '../../../data/plans.json'
import TextHelper from '../../../utils/text'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'
import NavigationHelper from '../../../utils/navigation'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {differenceInDays, isAfter, isBefore, isWithinInterval, parseISO} from 'date-fns'

const DATA = 'data'

const MainLayout = ({item}) => {
    const title = item.getTitle()
    const text = useNamespaceTranslations('titles')
    const layoutText = useNamespaceTranslations('main-layout')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const isFirstLogin = useSelector(state => state.resources.isFirstLogin)
    const modalIsActive = useSelector(state => state.voucher.modalIsActive)
    const globalInfo = useSelector(state => state.sidebar.globalInfo)
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const getMonitoringData = useSelector(state => state.monitoring.getData)
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const lastActiveDay = localStorage.getItem(localStorageKeys.LAST_ACTIVE_DAY)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const dispatch = useDispatch()
    const location = useLocation()
    const plan = localStorage.getItem(localStorageKeys.PLAN)
    const trial = localStorageManager.get(localStorageKeys.TRIAL)
    const now = new Date()
    const isTrialStarted = trial && now > new Date(trial?.started)
    const trialExpiresDate = trial?.expires ? parseISO(trial.expires) : null
    const remainingTrialDay = trialExpiresDate ? differenceInDays(trialExpiresDate, now) : 0
    const isExpired = trialExpiresDate ? isAfter(now, trialExpiresDate) : false
    const activeSubscription = subscriptions?.data?.subscriptions?.find(subscription => !!subscription?.active)
    const noPlan = plan === plans.free_v2.key || (plan === plans.free_v1.key && isTrialStarted && !isExpired)
    const formattedCurrentDate = formatDate(new Date(), DateFormats.DOT_DMY)

    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)

    const {
        maintenanceWindow,
        showInfoMsg,
        isFeedbackButtonEnabled,
        isRegionUnified
    } = envConfig
    const [startDate, endDate] = maintenanceWindow.split(',')

    const maintenanceStart = startDate ? parseISO(startDate) : null
    const maintenanceEnd = endDate ? parseISO(endDate) : null
    const showMaintenancePeriodMessage = showInfoMsg && maintenanceEnd && isBefore(now, maintenanceEnd)

    const [trialMsg, setTrialMsg] = useState('')
    const infoMsgsRef = useRef()

    const usageLimitsAdvanced = limitUsageManager(usageLimitsData?.limit_modules?.monitoring?.module, usageLimitsData?.limit_keys?.server_advanced_resource_count?.limit_key, organisationLimitUsages)

    const isFeedbackButtonAllowed = path => {
        if (
            isFeedbackButtonEnabled &&
            !isFirstLogin &&
            !routes.monitoring.isSame(path)
        ) {
            return true
        }
    }

    const closeModal = () => {
        dispatch(setVoucherModalIsActive(!modalIsActive))
        dispatch(setDisableTutorial(false))
        dispatch(setVoucherData({}))
    }

    const setWarningMessage = (layoutText) => {
        if (!isTrialStarted || (isExpired && plan !== plans.free_v2.key) || (!!activeSubscription && !activeSubscription?.in_trial) || plan.includes(plans.appsumo_key)) {
            setTrialMsg(null)
            return
        }

        if (isExpired) {
            setTrialMsg(layoutText.trial_expired_info)
            return
        }

        remainingTrialDay === 0
            ? setTrialMsg(layoutText.trial_ends_today)
            : setTrialMsg(TextHelper.replaceAllChars(layoutText.trial_info, ':number', remainingTrialDay))
    }

    const getMaintenanceWindowsMsg = () => {
        const isInMaintenance = maintenanceStart && maintenanceEnd && isWithinInterval(now, {start: maintenanceStart, end: maintenanceEnd})

        if (isInMaintenance) {
            return TextHelper.replaceAllChars(layoutText.maintenance_windows_msg,  ':end_date',
                formatWithGMT(maintenanceEnd, DateFormats.FULL_WEEKDAY))
        }

        if (showMaintenancePeriodMessage) {
            return TextHelper.replaceAllChars(layoutText.info_msg, ':start_date', formatWithGMT(maintenanceStart, DateFormats.FULL_WEEKDAY))
        }
    }

    useEffect(() => {
        document.title = `${text.ping_home_console} | ${title}`
        window.scrollTo(0, 0)
        getMonitoringData && dispatch(resetQuery())
    }, [title])

    useEffect(() => {
        const makeAction = async() => {
            const success = await dispatch(logCustomerIsActive())
            if (success) {
                localStorage.setItem(localStorageKeys.LAST_ACTIVE_DAY, formattedCurrentDate)
            }
            return
        }

        if (isAuthenticated && (!lastActiveDay || lastActiveDay !== formattedCurrentDate)) {
            makeAction()
        }
    }, [isAuthenticated, location?.pathname])

    useEffect(() => {
        if (DATA in subscriptions) {
            setWarningMessage(layoutText)
        }
    }, [subscriptions, layoutText])

    const noPlanFreeV2Text = TextHelper.replaceAllChars(layoutText.no_plan_free_v2, ':start_trial', isExpired ? '' : layoutText.start_trial)
    const isUsageMetricsExceeded = usageLimitsAdvanced?.usage > usageLimitsAdvanced?.value && plan !== plans.free_v2.key && routes.server_resource.containsId(location?.pathname)
    const maintenanceWindowsMsg = getMaintenanceWindowsMsg()

    return (
        <>
            <div className='banner' ref={infoMsgsRef}>
                {!maintenanceWindowsMsg && isRegionUnified && showInfoMsg &&
        <div className='plans-info green-bg'>
            <span className='plans-info-text' dangerouslySetInnerHTML={{__html: layoutText.maintenance_completed_msg}}/>
        </div>
                }
                {isUsageMetricsExceeded && (
                    <div className='plans-info exceeded-metrics'>
                        <span className='plans-info-text'>
                            {layoutText.exceeded_metrics_msg}
                            <Link to={NavigationHelper.getLocalizedPath(routes.plans.paths)}> {layoutText.upgrade_plan}</Link>{layoutText.or_disable_metrics}
                        </span>
                    </div>
                )}
                {(noPlan || maintenanceWindowsMsg || showMaintenancePeriodMessage) && (
                    <div className='plans-info'>
                        <span className='plans-info-text'>
                            {noPlan ? <>
                                {TextHelper.replaceReactPlaceholders(plan === plans.free_v2.key ? noPlanFreeV2Text : layoutText.no_plan_free_v1, ':here',
                                    <Link to={NavigationHelper.getLocalizedPath(routes.plans.paths)}> {layoutText.here}</Link>
                                ) }
                            </> :
                                <span dangerouslySetInnerHTML={{__html: maintenanceWindowsMsg ||
                                TextHelper.replaceAllChars(layoutText.info_msg, ':start_date', maintenanceStart)}}/>
                            }
                        </span>
                    </div>
                )}
            </div>
            <Alert/>
            <Modal isOpen={modalIsActive} className={classNames('voucher-modal', {
                dark: isDarkMode
            })} closeModal={closeModal}>
                <VoucherModal closeModal={closeModal}/>
            </Modal>
            <SubscriptionSession/>
            <Authorization/>
            {isFeedbackButtonAllowed(item.path) && (
                <>
                    <div className="feedback-btn">
                        <span className="button"
                            onClick={() => dispatch(setFeedbackModalIsActive(true))}>
                            <PenIcon/>
                            <span className="text">{text.feedback}</span>
                        </span>
                    </div>
                    <Feedback/>
                </>
            )}
            {globalInfo && <InfoLine infoText={globalInfo}/>}
            <div className="container" style={{minHeight: window.innerHeight}}>
                <Sidebar infoMsgsHeight={infoMsgsRef?.current?.offsetHeight}  trialMessage={trialMsg}/>
                {isAuthenticated ? <item.component/> : <ContentLoader/>}
            </div>
        </>
    )
}

export default MainLayout
